import React, { useRef, useEffect, lazy, Suspense } from 'react';
import { CheckCircle2, Ruler } from 'lucide-react';
import type { JourneyMedia } from '../../utils/journeyFirestore';

const TimberFrameScene = lazy(() => import('../testimonials/TimberFrameScene'));

const DEFAULT_VIDEOS = {
  droneDesign: '/videos/journey-drone-design.mp4',
  cnc: '/videos/journey-cnc.mp4',
  crewRaising: '/videos/journey-crew-raising.mp4',
};

export type StageMediaType = '3d' | 'drone-design' | 'engineering' | 'cnc' | 'crew-raising';

export function getStageMediaType(stage: number): StageMediaType {
  if (stage <= 2) return '3d';
  if (stage <= 4) return 'drone-design';
  if (stage === 5) return 'engineering';
  if (stage === 6) return 'cnc';
  return 'crew-raising';
}

// ─── Video Layer (muted, loop, object-fit cover) ─────────────────────────────

interface JourneyVideoProps {
  src: string;
  isActive: boolean;
  className?: string;
}

const JourneyVideo: React.FC<JourneyVideoProps> = ({ src, isActive, className }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [isActive]);

  return (
    <div className={`journey-stage-video ${className ?? ''} ${isActive ? 'is-active' : ''}`}>
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        className="journey-stage-video-el"
      />
    </div>
  );
};

// ─── Engineering Model Overlay (stage 5) ─────────────────────────────────────

interface EngineeringOverlayProps {
  isActive: boolean;
}

const EngineeringOverlay: React.FC<EngineeringOverlayProps> = ({ isActive }) => (
  <div className={`journey-engineering-overlay ${isActive ? 'is-active' : ''}`}>
    <div className="journey-engineering-badge">
      <CheckCircle2 size={20} />
      <span>Engineered</span>
    </div>
    <div className="journey-engineering-dims">
      <Ruler size={14} />
      <span>2D plans approved</span>
    </div>
    <div className="journey-engineering-grid" aria-hidden />
  </div>
);

// ─── Stage Media Container ─────────────────────────────────────────────────

export interface JourneySceneProps {
  assemblyProgress: number;
  activeStage: number;
  isHovering?: boolean;
  isDragging?: boolean;
  mousePosition?: { x: number; y: number };
  modelRotation?: { x: number; y: number };
}

interface JourneyStageMediaProps {
  activeStage: number;
  /** Media URLs from Firestore; falls back to defaults when missing */
  media?: JourneyMedia | null;
  /** Props for 3D scene (stages 1–2 and 5) */
  sceneProps: JourneySceneProps;
  show3d?: boolean;
  /** Optional custom scene fallback (e.g. loading spinner) */
  sceneFallback?: React.ReactNode;
}

/**
 * Wraps the model area and switches content by stage:
 * 1–2: 3D model (model3d)
 * 3–4: Drone video (design/site)
 * 5: 3D model (model3dEngineering) + engineering overlay
 * 6: CNC machine video
 * 7: Crew raising drone video
 */
export const JourneyStageMedia: React.FC<JourneyStageMediaProps> = ({
  activeStage,
  media,
  sceneProps,
  show3d = true,
  sceneFallback = null,
}) => {
  const type = getStageMediaType(activeStage);
  const showModel3d = type === '3d';
  const showEngineering = type === 'engineering';
  const showDroneDesign = type === 'drone-design';
  const showCnc = type === 'cnc';
  const showCrewRaising = type === 'crew-raising';

  const model3d = media?.model3d || '/models/bent.glb';
  const model3dEngineering = media?.model3dEngineering || media?.model3d || '/models/bent.glb';
  const droneDesign = media?.droneDesign || DEFAULT_VIDEOS.droneDesign;
  const cnc = media?.cnc || DEFAULT_VIDEOS.cnc;
  const crewRaising = media?.crewRaising || DEFAULT_VIDEOS.crewRaising;

  /* Single 3D instance: only mount when stages 1–2 or 5 (avoids two canvases + no 3D during video stages) */
  const show3dScene = showModel3d || showEngineering;
  const activeModelPath = showEngineering ? model3dEngineering : model3d;

  return (
    <div className="journey-stage-media">
      {/* 3D model – stages 1–2 and 5 (single Canvas instance) */}
      {show3d && show3dScene && (
        <div className={`journey-stage-media-layer journey-stage-3d journey-stage-engineering ${show3dScene ? 'is-active' : ''}`}>
          <Suspense fallback={sceneFallback}>
            <TimberFrameScene {...sceneProps} modelPath={activeModelPath} />
          </Suspense>
          <EngineeringOverlay isActive={showEngineering} />
        </div>
      )}

      {/* Drone video – stages 3–4 */}
      <JourneyVideo
        src={droneDesign}
        isActive={showDroneDesign}
        className="journey-stage-media-layer"
      />

      {/* CNC video – stage 6 */}
      <JourneyVideo
        src={cnc}
        isActive={showCnc}
        className="journey-stage-media-layer"
      />

      {/* Crew raising video – stage 7 */}
      <JourneyVideo
        src={crewRaising}
        isActive={showCrewRaising}
        className="journey-stage-media-layer"
      />
    </div>
  );
};
